<script setup lang="ts">
import {reactive, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {Button, Card, ColorPicker, FileUpload, InputText, Label, Textarea} from "@/Components/Library";
import {PageTitle} from "@/Components/Global";
import {AnonymizationSelect, ResetButton} from "@/Components/Forms";
import {useStorage} from "@vueuse/core";
import {deleteIcon as deleteIconViaApi, getGroup, storeGroup, updateGroup} from "@/Services/groups";
import {useMainStore} from "@/Stores/store";
import {saving} from "@/Composables/api";
import LaunchHeader from "../Questions/Launch/LaunchHeader.vue";
import PageContainer from "@/Components/Global/PageContainer.vue";

const $store = useMainStore();
const currentRoute = useRoute();
const router = useRouter();
const contacts = ref([]);
const state = reactive({
    show: {
        addContactDialog: false,
    },
});

const defaultGroup = {
    id: currentRoute.params?.group || null,
    name: null,
    description: null,
    important_info: null,
    color: null,
    anonymization: "full_name",
    tags: [],
};

const group = useStorage("groupForm", defaultGroup);
const members = ref([]);
const selectedMembers = ref([]);
const groupIconUploadRef = ref<typeof FileUpload>();

if (currentRoute.params?.group) {
    getGroup(currentRoute.params.group).then((response) => {
        Object.assign(group.value, response.data);
        group.value.color = response.data.color?.slug;
        selectedMembers.value = response.data.members.map((member) => member.id);
    });
} else {
    if (group.value.id) {
        group.value = defaultGroup;
    }
}
const save = async () => {
    try {
        let postData = {
            id: group.value.id,
            name: group.value.name,
            description: group.value.description,
            important_info: group.value.important_info,
            color: group.value.color,
            tags: group.value.tags.map((tag) => tag.name),
        };
        if (!currentRoute.params?.group) {
            postData.anonymization = group.value.anonymization;
        }
        let newGroup;
        if (currentRoute.params.group) {
            newGroup = await updateGroup(currentRoute.params.group, postData);
        } else {
            const {data} = await storeGroup(postData);
            newGroup = data;
            const groupIndex = $store.groups.findIndex((group) => group.name.localeCompare(newGroup.name) > 0);
            $store.groups.splice(groupIndex === -1 ? $store.groups.length : groupIndex, 0, newGroup);
        }
        Object.assign(group.value, newGroup);

        if (groupIconUploadRef.value.files.length) {
            await groupIconUploadRef.value?.startUpload();
        }

        $store.toastMessages.push({
            severity: "success",
            summary: `Group ${newGroup.name} has been ${currentRoute.params.group ? "updated" : "created"}`,
            life: 5000,
        });
        if (!selectedMembers.value.length) {
            await router.push({
                name: "groups.edit.members",
                params: {group: newGroup.id},
                query: {...currentRoute.query, from: "form"},
            });
        } else {
            // TODO return group from store/update and set in $store
            $store.group = newGroup;
            await router.push({name: "feed", params: {group: newGroup.id}});
        }
    } catch (e) {
        console.log(e);
    }
};

const deleteIcon = async () => {
    await deleteIconViaApi(group.value.id, group.value.icon.id);
    group.value.icon = null;
};
</script>
<template>
    <PageContainer class="max-w-5xl">
        <div class="m-container mb-2" v-if="currentRoute.query.launch">
            <LaunchHeader @cancel="router.push({name: 'questions.index'})">
                <div class="pb-3">
                    <h3 class="font-medium">Create a group</h3>
                    <p class="text-sm">this will be the audience for your post</p>
                </div>
            </LaunchHeader>
        </div>
        <PageTitle
            class="mb-2"
            :title="`${currentRoute.params?.group ? 'Update' : 'Create a'} group`"
            :route="{name: 'feed', params: group.id}"
        >
            <ResetButton @reset="group = {...defaultGroup}" />
        </PageTitle>
        <form @submit.prevent="save">
            <Card class="mb-3">
                <div class="mb-4">
                    <Label for="name" class="mb-1 font-semibold" required>Name</Label>
                    <InputText
                        id="name"
                        name="name"
                        placeholder="Enter a name for your group"
                        fluid
                        required
                        v-model="group.name"
                    />
                </div>
                <div class="mb-4 flex flex-col">
                    <Label for="description" class="mb-1 font-semibold">Description</Label>
                    <Textarea
                        id="description"
                        name="description"
                        placeholder="Enter a description for your group"
                        fluid
                        auto-resize
                        v-model="group.description"
                    />
                </div>
                <div class="mb--6 flex flex-col">
                    <Label for="important_info" class="mb-1 font-semibold">Important Info</Label>
                    <Textarea
                        id="important_info"
                        name="important_info"
                        placeholder="Add any important information for group members"
                        fluid
                        auto-resize
                        v-model="group.important_info"
                    />
                </div>
            </Card>
            <Card class="mb-3">
                <div class="mb--6">
                    <h3 class="mb-2 font-bold">Icon & color</h3>
                    <div class="mb-2 flex flex-col">
                        <div
                            :key="group.icon?.url ?? 0"
                            v-if="group.icon?.url"
                            class="relative mx-auto mb-3 aspect-square max-h-48 max-w-48"
                        >
                            <Button
                                class="!absolute right-0 top-0 z-20 !p-1 text-red-600 hover:bg-red-600 hover:text-white"
                                :icon="{
                                    icon: 'mdi:close',
                                    width: '20',
                                }"
                                @click.stop="deleteIcon"
                            />
                            <div class="h-full w-full overflow-hidden rounded-xl">
                                <img
                                    :src="group.icon?.url"
                                    :alt="`${group.name} group icon`"
                                    class="h-full w-full object-cover"
                                />
                            </div>
                        </div>
                        <FileUpload
                            ref="groupIconUploadRef"
                            collection="icon"
                            :parent-id="group.id"
                            parent-type="group"
                            accept="image/*"
                        />
                    </div>
                    <ColorPicker v-model="group.color" />
                </div>
            </Card>
            <Card class="mb-3" v-if="!currentRoute.params.group">
                <div class="mb--6">
                    <h3 class="mb-2 font-bold">Anonymization</h3>
                    <AnonymizationSelect v-model="group.anonymization" />
                </div>
            </Card>
            <!--            <Card class="mb-3">-->
            <!--                <TagSelect v-model="group.tags" />-->
            <!--            </Card>-->
            <div class="flex flex-col items-center">
                <Button
                    :loading="!!saving"
                    type="submit"
                    class="w-full bg-askr-green text-white"
                    icon="fluent:checkmark-12-regular"
                >
                    {{ currentRoute.params?.group ? "Update" : "Create" }} group
                </Button>
                <RouterLink v-if="group.id" :to="{name: 'groups.edit.members', params: {group: group.id}}">
                    <Button>{{ group.members?.length ? "Manage" : "Add" }} members</Button>
                </RouterLink>
            </div>
        </form>
        <!--        <div v-auto-animate>-->
        <!--            <div v-if="tab === 'group'">-->
        <!--            </div>-->
        <!--            <div v-if="tab === 'members'">-->
        <!--                <div class="m-container">-->
        <!--                    <div class="mb-2 flex items-center gap-2">-->
        <!--                        <Avatar-->
        <!--                            class="h-8 w-8 text-xs"-->
        <!--                            :class="[`bg-${group.color?.class}`, group?.color?.class === 'white' ? 'text-black' : 'text-white']"-->
        <!--                            :label="group.initials"-->
        <!--                        />-->
        <!--                        <h2 class="text&#45;&#45;lg font-bold sm:text-2xl">-->
        <!--                            {{ group.name }}-->
        <!--                        </h2>-->
        <!--                        <div class="ml-auto">-->
        <!--                            <Button-->
        <!--                                class="h-10 w-10 border bg-stone-50 !p-0 shadow-sm hover:bg-white"-->
        <!--                                icon="lucide:edit"-->
        <!--                                @click="tab = 'group'"-->
        <!--                            />-->
        <!--                        </div>-->
        <!--                    </div>-->
        <!--                    &lt;!&ndash;    <TagList :tags="question.tags" class="mb-3" />&ndash;&gt;-->
        <!--                    <div class="mb-3 flex flex-col items-start">-->
        <!--                        <div class="flex items-center gap-1 text-stone-500">-->
        <!--                            <Icon icon="mdi:text-box-outline" width="24" />-->
        <!--                            <h4 class="text-sm font-bold">Description</h4>-->
        <!--                        </div>-->
        <!--                        <p>{{ group.description }}</p>-->
        <!--                    </div>-->
        <!--                    <ContactSelect-->
        <!--                        :contacts="contacts"-->
        <!--                        v-model="selectedMembers"-->
        <!--                        @save="syncMembers"-->
        <!--                        @addContact="state.show.addContactDialog = true"-->
        <!--                    />-->
        <!--                </div>-->
        <!--            </div>-->
        <!--        </div>-->
        <!--        <AddContactDrawer v-model:visible="state.show.addContactDialog" :group="group" @invited="" />-->
    </PageContainer>
</template>
<style scoped></style>
